import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { forkJoin, of, Observable, from } from 'rxjs';
import { switchMap, concatMap, tap, mapTo, finalize } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  PssAuthService, PssSession, PssPlayerDetails, PssCharacterDesign, PssCrewMember,
  PssRoomDesign, PssAmmoDesign, PssShipDesign, PssItemDesign, PssInventoryItem,
  PssConditionType, PssActionType, PssCharacterAction, PssRoom, PssRoomAction,
  PssStarSystem, PssBattle, PssBoostGaugeInfo, PssRushTarget, PssResearchDesign,
  PssPollingTask,
} from '../../services/pss-auth.service';
import { spriteIdOrEmpty } from './pss-display-utils';
import { OverviewTabComponent } from './overview-tab/overview-tab.component';
import { BattlesTabComponent } from './battles-tab/battles-tab.component';
import { CrewTabComponent } from './crew-tab/crew-tab.component';
import { GalaxyTabComponent } from './galaxy-tab/galaxy-tab.component';
import { RoomsTabComponent } from './rooms-tab/rooms-tab.component';
import { FleetTabComponent } from './fleet-tab/fleet-tab.component';
import { FleetChatTabComponent } from './fleet-chat-tab/fleet-chat-tab.component';
import { FriendsTabComponent } from './friends-tab/friends-tab.component';
import { InventoryTabComponent } from './inventory-tab/inventory-tab.component';
import { TournamentTabComponent } from './tournament-tab/tournament-tab.component';
import { DashboardNavComponent } from './dashboard-nav/dashboard-nav.component';
import { AutomationModalComponent } from './automation-modal/automation-modal.component';
import { CrewDetailModalComponent } from './crew-detail-modal/crew-detail-modal.component';
import { CompareTarget } from './ai-editor/ai-editor.component';
import { AiImportRule, AiImportPayload } from './ai-editor/ai-editor.component';
import { RoomDetailModalComponent } from './room-detail-modal/room-detail-modal.component';
import { UseItemModalComponent } from './use-item-modal/use-item-modal.component';
import { PssSpriteComponent } from '../pss-sprite/pss-sprite.component';
import { SupportService, SupportStatus } from '../../services/support.service';
import { FeatureFlagService, FEATURE_FLAGS } from '../../services/feature-flag.service';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    OverviewTabComponent,
    BattlesTabComponent,
    CrewTabComponent,
    GalaxyTabComponent,
    RoomsTabComponent,
    FleetTabComponent,
    FleetChatTabComponent,
    FriendsTabComponent,
    InventoryTabComponent,
    TournamentTabComponent,
    DashboardNavComponent,
    AutomationModalComponent,
    CrewDetailModalComponent,
    RoomDetailModalComponent,
    UseItemModalComponent,
    PssSpriteComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
})
export class DashboardComponent implements OnInit, OnDestroy {

  session: PssSession | null = null;
  details: PssPlayerDetails | null = null;
  characterDesigns = new Map<string, PssCharacterDesign>();
  roomDesigns = new Map<string, PssRoomDesign>();
  loadingRanking = false;
  rankHistory: { ranking: string; totalUsers: string; totalActiveUsers: string; recordedAt: string }[] = [];
  rankHistoryOpen = false;
  rankHistoryLoading = false;
  rankSource: 'cache' | 'live' | null = null;

  get pilotFrame(): string | undefined { return this.details?.frame || this.session?.frame; }
  get pilotPin(): string | undefined { return this.details?.pin || this.session?.pin; }
  get pilotRibbon(): string | undefined { return this.details?.ribbon || this.session?.ribbon; }
  get hasPilotBadge(): boolean { return !!(this.pilotFrame || this.pilotPin || this.pilotRibbon); }
  researchDesigns = new Map<string, PssResearchDesign>();
  shipDesigns = new Map<string, PssShipDesign>();
  itemDesigns = new Map<string, PssItemDesign>();
  ammoDesigns = new Map<string, PssAmmoDesign>();
  selectedAmmoId: string | null = null;

  get donatedCrew(): PssCrewMember[] {
    return this.details?.crew.filter(c => c.donated) ?? [];
  }
  conditionTypes = new Map<string, PssConditionType>();
  actionTypes = new Map<string, PssActionType>();
  starSystems = new Map<string, PssStarSystem>();

  selectedCrew: PssCrewMember | null = null;
  showCrewAI = true;
  aiNewConditionId = '';

  useItemCharacters: PssCrewMember[] = [];
  aiNewActionId = '';
  aiSaving = false;
  aiError = '';
  compareAiSaving = false;
  compareAiError = '';

  private aiDeleteQueue: PssCharacterAction[] = [];
  private aiDeleteInProgress = false;
  private roomAiDeleteQueue: PssRoomAction[] = [];
  private roomAiDeleteInProgress = false;

  loading = true;
  error = '';
  activeTab: 'overview' | 'crew' | 'rooms' | 'galaxy' | 'fleet' | 'fleet-chat' | 'friends' | 'battles' | 'inventory' | 'tournament' = 'overview';
  pendingFriendCount = 0;

  private readonly DASHBOARD_TABS = ['overview', 'crew', 'rooms', 'galaxy', 'fleet', 'fleet-chat', 'friends', 'battles', 'inventory', 'tournament'] as const;

  battles: PssBattle[] = [];
  battlesLoading = false;
  battlesError = '';
  viewedUtcDayOffset = 0;

  // Automation modal state (scheduled tasks)
  showAutomationModal = false;
  automationLoading = false;
  automationError = '';
  autoTaskSaving = false;

  // User automation toggles
  autoEnableBattleSync = false;
  autoEnableDonationExtend = false;
  autoDonationSelectedIds = new Set<string>();
  autoScheduleType: 'interval' | 'cron' | 'times' = 'cron';
  autoIntervalSec = 86400;
  autoCronExpression = '0 2 * * *';
  autoScheduleTimes: string[] = [];
  autoScheduleTimeInput = '02:00';

  // Cached user polling task ids
  autoBattleTaskId: number | null = null;
  autoDonationTaskId: number | null = null;

  automationTasks: PssPollingTask[] = [];

  // Newly-earned item tracking (designIds earned by activating items this session)
  invNewItems = new Set<string>();

  // Use-item modal state
  useItemModal: PssInventoryItem | null = null;
  useItemCount = 1;
  useItemProgress = 0;
  useItemTotal = 0;
  useItemRunning = false;
  useItemDone = false;
  useItemError = '';
  useItemRetryMsg = '';
  useItemRetryCountdown = 0;
  useItemResults: { rewardDesignId: string; rewardQty: number }[] = [];
  useItemResultsGrouped = true;
  useItemSelectedReward: string | null = null;
  useItemFireCommon = false;
  useItemFireElite = false;
  useItemFiredIds = new Set<string>();
  private useItemQueue: string[] = [];
  private useItemRetryTimer: any = null;

  // Items whose activation yields crew and support the "Fire all 1★/2★ crew" options
  private static readonly FIREABLE_LOTTERY_ITEM_NAMES = new Set(['conscription lottery']);

  selectedRoom: PssRoom | null = null;
  roomAiNewConditionId = '';
  roomAiNewActionId = '';
  roomAiSaving = false;
  roomAiError = '';

  // ── Info flyout ────────────────────────────────────────────────────────────
  infoFlyoutOpen = false;

  toggleInfoFlyout(e: MouseEvent): void {
    e.stopPropagation();
    this.infoFlyoutOpen = !this.infoFlyoutOpen;
  }

  @HostListener('document:click')
  closeInfoFlyout(): void {
    this.infoFlyoutOpen = false;
  }

  // ── Idle tracking ──────────────────────────────────────────────────────────
  // If the page is reloaded after this much inactivity, the user is sent back
  // to the account picker instead of silently re-logging into the last account.
  private static readonly IDLE_THRESHOLD_MS = 5 * 60 * 1000;
  private static readonly ACTIVITY_KEY = 'pss_last_activity';

  @HostListener('document:click')
  @HostListener('document:keydown')
  @HostListener('document:touchstart')
  private markActivity(): void {
    try { localStorage.setItem(DashboardComponent.ACTIVITY_KEY, String(Date.now())); } catch {}
  }

  private wasRecentlyActive(): boolean {
    try {
      const raw = localStorage.getItem(DashboardComponent.ACTIVITY_KEY);
      if (!raw) return false;
      const ts = parseInt(raw, 10);
      if (!Number.isFinite(ts)) return false;
      return Date.now() - ts < DashboardComponent.IDLE_THRESHOLD_MS;
    } catch {
      return false;
    }
  }

  // ── Support / Tip-jar ──────────────────────────────────────────────────────
  supportLoading = false;
  supportStatus: SupportStatus | null = null;
  supportClaimToken: string | null = null;
  supportClaimExpiresAt: Date | null = null;
  supportError = '';
  lastSupportUrl: string | null = null;

  // ── Boost gauge ─────────────────────────────────────────────────────────────
  showBoostModal = false;
  selectedRushTargetId: string | null = null;
  boostApplying = false;
  boostError = '';
  boostResultMsg = '';
  showBoostConfetti = false;

  get effectiveSupportTier(): string {
    return this.supportStatus?.effectiveTier?.name ?? 'Free';
  }

  get hasSupporterBadge(): boolean {
    if (!this.supportStatus) return false;
    if (this.supportStatus.recurringActive) return true;
    if (this.supportStatus.oneTimeTotalUsd > 0) return true;
    return false;
  }

  get leagueInfo(): { name: string; cssClass: string } {
    const trophies = Number(this.details?.trophies ?? NaN);
    if (!Number.isFinite(trophies) || trophies < 500) {
      return { name: 'Unranked', cssClass: 'league-unranked' };
    }
    if (trophies < 1100) {
      return { name: 'Bronze', cssClass: 'league-bronze' };
    }
    if (trophies < 1400) {
      return { name: 'Silver', cssClass: 'league-silver' };
    }
    if (trophies < 2000) {
      return { name: 'Gold', cssClass: 'league-gold' };
    }
    if (trophies < 2600) {
      return { name: 'Platinum', cssClass: 'league-platinum' };
    }
    if (trophies < 3200) {
      return { name: 'Diamond', cssClass: 'league-diamond' };
    }
    if (trophies < 4100) {
      return { name: 'Epic', cssClass: 'league-epic' };
    }
    if (trophies < 5000) {
      return { name: 'Champion', cssClass: 'league-champion' };
    }
    return { name: 'Legend', cssClass: 'league-legend' };
  }

  get leagueName(): string {
    return this.leagueInfo.name;
  }

  get leagueCssClass(): string {
    return this.leagueInfo.cssClass;
  }

  get leagueIconSpriteId(): string {
    const league = this.leagueName.toLowerCase();
    if (!league || !this.itemDesigns?.size) return '';

    const candidate = Array.from(this.itemDesigns.values()).find(item => {
      const name = item.name?.toLowerCase() ?? '';
      return name.includes(league) && name.includes('league') && item.logoSpriteId;
    });

    return candidate?.logoSpriteId ?? '';
  }

  private leagueColorMap: Record<string, string> = {
    Unranked: '#4d4d4d',
    Bronze: '#b87333',
    Silver: '#c0c0c0',
    Gold: '#d4af37',
    Platinum: '#93c5fd',
    Diamond: '#6dd4ff',
    Epic: '#8b5cf6',
    Champion: '#f5c200',
    Legend: '#7f0000',
  };

  /** Generated SVG fallback used when no league sprite is available. */
  get leagueIconFallbackUrl(): string {
    const league = this.leagueName;
    if (!league) return '';

    const fill = this.leagueColorMap[league] ?? '#444';
    const text = league.substring(0, 1).toUpperCase();
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect x="0" y="0" width="64" height="64" rx="12" ry="12" fill="${fill}" fill-opacity="0.35"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Helvetica,Arial,sans-serif" font-size="30" fill="#ffffff" fill-opacity="0.9">${text}</text></svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  }

  get playerRank(): number | null {
    const rank = this.details?.globalRanking;
    if (!rank) return null;
    const parsed = parseInt(rank.replace(/,/g, ''), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  get rankTierClass(): string | null {
    const rank = this.playerRank;
    if (!rank) return null;
    if (rank <= 10) return 'rank-top10';
    if (rank <= 50) return 'rank-top50';
    if (rank <= 100) return 'rank-top100';
    if (rank <= 500) return 'rank-top500';
    if (rank <= 1000) return 'rank-top1000';
    return null;
  }

  get rankLabel(): string {
    return this.playerRank ? `Rank #${this.playerRank.toLocaleString()}` : '';
  }

  toggleRankHistory(): void {
    this.rankHistoryOpen = !this.rankHistoryOpen;
    if (this.rankHistoryOpen && this.rankHistory.length === 0) {
      this.loadRankHistory();
    }
  }

  private loadStoredRanking(): void {
    if (!this.session) return;
    this.auth.getStoredUserRanking(this.session.userId).subscribe(entry => {
      if (!entry || !this.details) return;
      this.details.globalRanking = entry.ranking;
      this.details.globalRankTotalUsers = entry.totalUsers;
      this.details.globalRankTotalActiveUsers = entry.totalActiveUsers;
      this.rankSource = 'cache';
    });
  }

  private refreshLiveRanking(): void {
    if (!this.session) return;
    this.loadingRanking = true;
    this.auth.getUserRanking(this.session.accessToken).pipe(
      finalize(() => { this.loadingRanking = false; })
    ).subscribe({
      next: rank => {
        if (!this.details) return;
        if (rank.ranking) {
          this.details.globalRanking = rank.ranking;
          this.details.globalRankTotalUsers = rank.totalUsers;
          this.details.globalRankTotalActiveUsers = rank.totalActiveUsers;
          this.rankSource = 'live';
          const userId = this.session?.userId;
          if (userId) {
            this.auth.saveUserRanking(userId, rank.ranking, rank.totalUsers, rank.totalActiveUsers).subscribe();
          }
        }
      },
      error: () => {
        // Keep cached rank if available.
      }
    });
  }

  private loadRankHistory(): void {
    if (!this.session) return;
    this.rankHistoryLoading = true;
    this.auth.getUserRankingHistory(this.session.userId, 20).pipe(
      finalize(() => { this.rankHistoryLoading = false; })
    ).subscribe(history => {
      this.rankHistory = history;
    });
  }

  get automationFeatureEnabled(): boolean {
    return this.featureFlags.isEnabled(FEATURE_FLAGS.AUTOMATION);
  }

  get hasTournamentAnalysis(): boolean {
    return this.featureFlags.isEnabled(FEATURE_FLAGS.TOURNAMENT_ANALYSIS);
  }

  constructor(private auth: PssAuthService, private router: Router, private route: ActivatedRoute, private supportSvc: SupportService, public featureFlags: FeatureFlagService) {}

  private isValidTab(tab: string | null): tab is typeof this.activeTab {
    return tab !== null && (this.DASHBOARD_TABS as readonly string[]).includes(tab);
  }

  // ── Silent re-auth helper ─────────────────────────────────────────────────
  private reauthAndRun(action: () => void): void {
    this.auth.reauthenticate().subscribe({
      next: newSession => {
        this.session = newSession;
        action();
      },
      error: () => this.router.navigate(['/'])
    });
  }

  private initializeDashboard(): void {
    this.markActivity();
    this.refreshPendingFriendCount();
    this.refreshPlayerDetails();
  }

  private tryRestoreSessionFromSavedAccount(): void {
    // After a page reload, only auto-restore the last account if the user was
    // recently active. Otherwise (first load, or idle for too long) bounce to
    // the account picker so they consciously choose an account.
    if (!this.wasRecentlyActive()) {
      this.router.navigate(['/']);
      return;
    }
    this.auth.getSavedAccounts().subscribe({
      next: accounts => {
        if (!accounts || accounts.length === 0) {
          this.router.navigate(['/']);
          return;
        }
        const mostRecent = accounts.slice().sort((a, b) => b.lastLogin.localeCompare(a.lastLogin))[0];
        this.auth.loginWithAccount(mostRecent).subscribe({
          next: session => {
            this.session = session;
            this.initializeDashboard();
          },
          error: () => this.router.navigate(['/'])
        });
      },
      error: () => this.router.navigate(['/'])
    });
  }

  ngOnInit() {
    const requestedTab = this.route.snapshot.queryParamMap.get('tab');
    if (this.isValidTab(requestedTab)) {
      this.activeTab = requestedTab;
    }

    this.session = this.auth.currentSession;
    if (!this.session) {
      this.tryRestoreSessionFromSavedAccount();
      return;
    }

    this.initializeDashboard();
  }

  ngOnDestroy(): void {
    if (this.useItemRetryTimer) clearTimeout(this.useItemRetryTimer);
  }

  onManualRefresh(): void {
    // Force a fresh device login by resetting the cache, then reload player data.
    this.auth.lastAuthTime = 0;
    this.refreshPlayerDetails();
  }

  private refreshPlayerDetails(): void {
    if (!this.session) return;
    this.loading = true;
    this.error = '';

    this.auth.getPlayerDetails(this.session).subscribe({
      next: details => {
        this.applyPlayerDetails(details);
      },
      error: () => {
        this.loading = false;
        this.error = 'Could not refresh pilot data; please try again or re-login.';

        // Attempt silent recovery via reauth, then refetch once.
        this.reauthAndRun(() => {
          if (this.session) {
            this.auth.getPlayerDetails(this.session).subscribe({
              next: details => this.applyPlayerDetails(details),
              error: () => {
                this.loading = false;
                this.error = 'Session refresh failed; please reload and login again.';
              }
            });
          }
        });
      }
    });
  }

  private applyPlayerDetails(details: PssPlayerDetails): void {
    this.details = details;
    this.loading = false;
    this.error = '';

    if (this.session) {
      // Keep saved account metadata in sync with latest fetched details.
      this.session.shipLevel = details.shipLevel || this.session.shipLevel;
      this.auth.currentSession = this.session;
      this.auth.updateAccountExtras(
        this.session.userId,
        details.trophies,
        details.allianceId || '',
        details.fleetName !== '\u2014' ? details.fleetName : '',
        details.shipLevel || this.session.shipLevel
      );
    }

    if (details.crew.length > 0 && this.session) {
      const srv = this.session.productionServer;
      this.auth.getCharacterDesigns(srv).subscribe(d => this.characterDesigns = d);
      this.auth.getRoomDesigns(srv).subscribe(d => this.roomDesigns = d);
      this.auth.getResearchDesigns(srv).subscribe(d => this.researchDesigns = d);
      this.auth.getShipDesigns(srv).subscribe(d => this.shipDesigns = d);
      this.auth.getItemDesigns(srv).subscribe(d => this.itemDesigns = d);
      this.auth.getAmmoDesigns(srv).subscribe(d => this.ammoDesigns = d);
      this.auth.getConditionTypes(srv).subscribe(d => this.conditionTypes = d);
      this.auth.getActionTypes(srv).subscribe(d => this.actionTypes = d);
      this.auth.getStarSystems(srv, this.session!.accessToken).subscribe(d => this.starSystems = d);
    }

    if (this.session) {
      this.supportSvc.getStatus(this.session.userId).subscribe({
        next: r => this.supportStatus = r.status,
        error: () => {}
      });
      this.featureFlags.loadForUser(
        this.session.userId,
        details.allianceId || ''
      ).subscribe();
      this.loadStoredRanking();
      this.refreshLiveRanking();
    }

    this.battles = details.battles;
    if (this.session) {
      this.battlesLoading = true;
      this.auth.persistBattles(details.battles).pipe(
        switchMap(() => this.auth.getBattles(this.session!.userId, 2000))
      ).subscribe({
        next: (allBattles: PssBattle[]) => {
          if (allBattles.length > 0) this.battles = allBattles;
          this.battlesLoading = false;
        },
        error: () => { this.battlesLoading = false; }
      });
    }

    // Refresh stored settings whenever the user details are loaded.
    this.loadAutomationSettings();
  }

  openAutomationModal(): void {
    if (!this.automationFeatureEnabled) return;
    this.showAutomationModal = true;
    this.loadAutomationSettings();
  }

  closeAutomationModal(): void {
    this.showAutomationModal = false;
    this.automationError = '';
  }

  private loadAutomationSettings(): void {
    if (!this.session) return;
    this.automationLoading = true;
    this.automationError = '';

    this.auth.listPollingTasks().subscribe({
      next: tasks => {
        this.automationTasks = tasks;

        const battleTask = tasks.find(t => t.taskType === 'user_battles');
        const donationTask = tasks.find(t => t.taskType === 'user_donation_extend');

        this.autoEnableBattleSync = !!battleTask?.isActive;
        this.autoBattleTaskId = battleTask?.id ?? null;

        this.autoEnableDonationExtend = !!donationTask?.isActive;
        this.autoDonationTaskId = donationTask?.id ?? null;

        if (battleTask) {
          this.autoScheduleType = battleTask.scheduleType as 'interval' | 'cron' | 'times';
          this.autoIntervalSec = battleTask.intervalSec || 86400;
          this.autoCronExpression = battleTask.cronExpression || '0 2 * * *';
          this.autoScheduleTimes = battleTask.scheduleTimes || [];
        } else if (donationTask) {
          this.autoScheduleType = donationTask.scheduleType as 'interval' | 'cron' | 'times';
          this.autoIntervalSec = donationTask.intervalSec || 86400;
          this.autoCronExpression = donationTask.cronExpression || '0 2 * * *';
          this.autoScheduleTimes = donationTask.scheduleTimes || [];
        }

        this.autoDonationSelectedIds.clear();
        if (donationTask && donationTask.targetId) {
          for (const id of donationTask.targetId.split(',').map(s => s.trim()).filter(Boolean)) {
            this.autoDonationSelectedIds.add(id);
          }
        }

        this.automationLoading = false;
      },
      error: err => {
        this.automationError = err?.error?.error || err?.message || 'Failed to load automation settings';
        this.automationLoading = false;
      }
    });
  }

  toggleDonationSelection(characterId: string, selected: boolean): void {
    if (selected) this.autoDonationSelectedIds.add(characterId);
    else this.autoDonationSelectedIds.delete(characterId);
  }

  addScheduleTime(): void {
    const normalized = this.normalizeScheduleTime(this.autoScheduleTimeInput);
    if (!normalized) {
      this.automationError = 'Invalid time format. Use HH:mm.';
      return;
    }
    if (!this.autoScheduleTimes.includes(normalized)) {
      this.autoScheduleTimes = [...this.autoScheduleTimes, normalized].sort();
    }
    this.autoScheduleTimeInput = '02:00';
    this.automationError = '';
  }

  removeScheduleTime(time: string): void {
    this.autoScheduleTimes = this.autoScheduleTimes.filter(t => t !== time);
  }

  private normalizeScheduleTime(value: string): string | null {
    if (!value || typeof value !== 'string') return null;
    const parts = value.trim().split(':');
    if (parts.length !== 2) return null;
    const hour = Number(parts[0]);
    const minute = Number(parts[1]);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return null;
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  saveAutomationSettings(): void {
    if (!this.session) return;
    if (!this.automationFeatureEnabled) {
      this.automationError = 'Automation feature is not enabled for your account.';
      return;
    }

    if (this.autoScheduleType === 'times' && this.autoScheduleTimes.length === 0) {
      this.automationError = 'Add at least one scheduled time for the selected schedule mode.';
      return;
    }

    const tasks: Array<Observable<any>> = [];

    const scheduleProps: any = {
      scheduleType: this.autoScheduleType,
      intervalSec: this.autoIntervalSec,
      cronExpression: this.autoCronExpression,
      scheduleTimes: this.autoScheduleType === 'times' ? this.autoScheduleTimes : undefined,
    };

    const existingBattle = this.automationTasks.find(t => t.taskType === 'user_battles');
    const existingDonation = this.automationTasks.find(t => t.taskType === 'user_donation_extend');

    if (this.autoEnableBattleSync) {
      const battlePayload = {
        taskType: 'user_battles',
        description: 'Auto battle log sync',
        targetId: this.session.userId,
        ...scheduleProps
      };
      if (existingBattle) {
        tasks.push(this.auth.updatePollingTask(existingBattle.id, { ...battlePayload, isActive: true }));
      } else {
        tasks.push(this.auth.createPollingTask(battlePayload));
      }
    } else if (existingBattle) {
      tasks.push(this.auth.updatePollingTask(existingBattle.id, { isActive: false }));
    }

    if (this.autoEnableDonationExtend) {
      if (this.autoDonationSelectedIds.size === 0) {
        this.automationError = 'Select at least one donated crew for auto-extension';
        return;
      }

      const characterCsv = Array.from(this.autoDonationSelectedIds).join(',');
      const donationPayload = {
        taskType: 'user_donation_extend',
        description: 'Auto crew donation extension',
        targetId: characterCsv,
        ...scheduleProps
      };
      if (existingDonation) {
        tasks.push(this.auth.updatePollingTask(existingDonation.id, { ...donationPayload, isActive: true }));
      } else {
        tasks.push(this.auth.createPollingTask(donationPayload));
      }
    } else if (existingDonation) {
      tasks.push(this.auth.updatePollingTask(existingDonation.id, { isActive: false }));
    }

    if (tasks.length === 0) {
      this.automationError = 'No automation changes to save';
      return;
    }

    this.autoTaskSaving = true;
    this.automationError = '';

    forkJoin(tasks).subscribe({
      next: () => {
        this.autoTaskSaving = false;
        this.closeAutomationModal();
        this.loadAutomationSettings();
      },
      error: err => {
        this.autoTaskSaving = false;
        this.automationError = err?.error?.error || err?.message || 'Failed to save automation settings';
      }
    });
  }

  formatNumber(val: string): string {
    const n = parseInt(val, 10);
    if (isNaN(n)) return val;
    return n.toLocaleString();
  }

  get viewedUtcDateLabel(): string {
    return this.viewedUtcDayOffset === 0
      ? 'Today (UTC)'
      : this.utcDayKeyForOffset(this.viewedUtcDayOffset);
  }

  get canViewNewerUtcDay(): boolean {
    return this.viewedUtcDayOffset > 0;
  }

  get dailyTrophySummary(): {
    positive: number;
    negative: number;
    balance: number;
  } {
    const dayKey = this.utcDayKeyForOffset(this.viewedUtcDayOffset);
    let positive = 0;
    let negative = 0;

    for (const battle of this.battles) {
      if (!this.isDailyPvpBattle(battle)) continue;

      const battleDay = this.toUtcDayKey(battle.battleDate || battle.battleEndDate || '');
      if (battleDay !== dayKey) continue;

      const delta = this.battleTrophyDelta(battle);
      if (delta === null || delta === 0) continue;

      const isAttack = battle.attackingUserId === this.session?.userId;
      if (delta > 0) {
        if (isAttack || this.canGainDefenseTrophies(battle)) positive += delta;
      } else {
        const isLegendary = (battle.leagueType || '').toLowerCase().includes('legendary');
        if (isAttack && isLegendary) continue;
        negative += Math.abs(delta);
      }
    }

    return {
      positive,
      negative,
      balance: positive - negative,
    };
  }

  prevUtcDay(): void {
    this.viewedUtcDayOffset += 1;
  }

  nextUtcDay(): void {
    if (this.viewedUtcDayOffset > 0) this.viewedUtcDayOffset -= 1;
  }

  private battleTrophyDelta(battle: PssBattle): number | null {
    if (!this.session?.userId || !battle.outcomeType) return null;

    const iAmAttacker = battle.attackingUserId === this.session.userId;
    const attackerWon = battle.outcomeType === 'Attacker Won';
    const iWon = iAmAttacker === attackerWon;

    const swing = Number(battle.winTrophyResult) || 0;
    if (swing <= 0) return null;
    return iWon ? swing : -swing;
  }

  private isDailyPvpBattle(battle: PssBattle): boolean {
    return (battle.battleType || '').toUpperCase() === 'PVP' && !battle.isStarBattle;
  }

  private canGainDefenseTrophies(battle: PssBattle): boolean {
    const isLegendaryLeague = (battle.leagueType || '').toLowerCase().includes('legendary');
    return !isLegendaryLeague;
  }

  private utcDayKeyForOffset(offsetDays: number): string {
    const now = new Date();
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    d.setUTCDate(d.getUTCDate() - offsetDays);
    return this.toUtcDayKey(d.toISOString());
  }

  private toUtcDayKey(value: string): string {
    const normalized = value.trim();
    if (!normalized) return '';

    // Treat timezone-agnostic ISO strings as UTC, not local time.
    const hasZone = /([zZ]|[+\-]\d\d:\d\d|[+\-]\d{4})$/.test(normalized);
    const d = new Date(hasZone ? normalized : normalized + 'Z');

    if (isNaN(d.getTime())) return '';
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  }

  switchAccount() {
    this.featureFlags?.clear?.();
    this.auth.logout();
    this.router.navigate(['/']);
  }

  onDashboardTabSelect(tab: string): void {
    const selected = tab as typeof this.activeTab;
    if (this.activeTab === selected) return;

    this.activeTab = selected;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: selected },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    if (selected === 'fleet') this.openFleetTab();
    else if (selected === 'fleet-chat') this.openFleetChatTab();
    else if (selected === 'friends') this.openFriendsTab();
  }

  openFleetTab(): void { this.activeTab = 'fleet'; }
  openFleetChatTab(): void { this.activeTab = 'fleet-chat'; }
  openFriendsTab(): void { this.activeTab = 'friends'; }
  openBattlesTab(): void { this.activeTab = 'battles'; }

  get boostGauge(): PssBoostGaugeInfo | null {
    return this.details?.boostGauge ?? null;
  }

  private refreshPendingFriendCount(): void {
    if (!this.session) return;
    this.auth.listFriends().subscribe({
      next: f => {
        this.pendingFriendCount = f.pending.length;
      },
      error: () => {
        this.pendingFriendCount = 0;
      }
    });
  }

  get canSeeBoostFeature(): boolean {
    return true;
  }

  private readonly boostGaugeDisabledNotice = 'Savy has deemed this feature to be in violation of their TOS. Please complain to Savy if you want this feature returned';

  private showBoostGaugeDisabledNotice(): void {
    globalThis.alert(this.boostGaugeDisabledNotice);
  }

  get rushTargets(): PssRushTarget[] {
    const raw = this.details?.rushTargets ?? [];
    if (!this.details) return raw;
    const nowMs = Date.now();
    return raw.map(t => {
      if (t.targetType === 'research') {
        const design = this.researchDesigns.get(t.designId ?? t.targetId);
        if (!design) return t;
        const remaining = t.remainingSeconds ?? this.deriveRemaining(t.startDate, design.researchTime, nowMs);
        return {
          ...t,
          label:          design.name || t.label,
          level:          design.level || t.level,
          logoSpriteId:   design.logoSpriteId || t.logoSpriteId,
          remainingSeconds: remaining,
        };
      }
      if (t.targetType === 'ship') {
        const design = this.shipDesigns.get(t.designId ?? '');
        if (!design) return t;
        const remaining = t.remainingSeconds ?? this.deriveRemaining(t.startDate, design.upgradeTime, nowMs);
        return {
          ...t,
          remainingSeconds: remaining,
          logoSpriteId: t.logoSpriteId || design.logoSpriteId || '',
        };
      }
      // room
      const room = this.details!.rooms.find(r => r.roomId === t.targetId);
      if (!room) return t;
      const currentDesign = this.roomDesigns.get(room.roomDesignId);
      const nextId = room.upgradeRoomDesignId ?? '';
      const nextDesign = nextId && nextId !== '0' ? this.roomDesigns.get(nextId) : null;
      const currentName = currentDesign?.name;
      const nextName = nextDesign?.name;
      const label = nextName && currentName
        ? (currentName + ' → ' + nextName)
        : (currentName || t.label);
      const remaining = t.remainingSeconds ?? this.deriveRemaining(t.startDate, nextDesign?.constructionTime ?? currentDesign?.constructionTime, nowMs);
      return { ...t, label, remainingSeconds: remaining };
    });
  }

  private deriveRemaining(startDate: string | undefined, durationSeconds: number | undefined, nowMs: number): number | null {
    if (!startDate || !durationSeconds) return null;
    const startMs = new Date(startDate.endsWith('Z') ? startDate : startDate + 'Z').getTime();
    if (isNaN(startMs)) return null;
    const endMs = startMs + durationSeconds * 1000;
    return Math.max(0, Math.floor((endMs - nowMs) / 1000));
  }

  get boostTargetsForModal(): PssRushTarget[] {
    return this.rushTargets.filter(t => {
      if (t.targetType === 'room') {
        const room = this.details?.rooms.find(r => r.roomId === t.targetId);
        const statusNorm = (room?.status ?? t.status ?? '').toLowerCase();
        const looksUpgrading =
          statusNorm.includes('upgrade') ||
          statusNorm.includes('build') ||
          statusNorm.includes('construct') ||
          (!!room?.upgradeRoomDesignId && room.upgradeRoomDesignId !== '0');
        const definitelyNotUpgrade = statusNorm.includes('manufactur') || statusNorm.includes('craft');
        return looksUpgrading && !definitelyNotUpgrade;
      }
      if (t.targetType === 'ship') {
        return true; // only added to rushTargets when UpgradeShipDesignId is non-zero
      }
      if (t.targetType === 'research') {
        const statusNorm = (t.status ?? '').toLowerCase();
        const activeResearch = statusNorm.includes('research') || statusNorm.includes('progress');
        const doneResearch = statusNorm.includes('complete') || statusNorm.includes('finish') || statusNorm.includes('done');
        return activeResearch && !doneResearch;
      }
      return false;
    });
  }

  get selectedRushTarget(): PssRushTarget | null {
    if (!this.selectedRushTargetId) return null;
    return this.boostTargetsForModal.find(t => (t.targetType + ':' + t.targetId) === this.selectedRushTargetId) ?? null;
  }

  get canApplyBoost(): boolean {
    return !!this.selectedRushTarget && (this.boostGauge?.hasBoostAmount ?? true);
  }

  openBoostModal(): void {
    this.showBoostGaugeDisabledNotice();
  }

  closeBoostModal(): void {
    if (this.boostApplying) return;
    this.showBoostModal = false;
  }

  selectRushTarget(target: PssRushTarget): void {
    this.selectedRushTargetId = target.targetType + ':' + target.targetId;
  }

  private formatRemainingTime(seconds: number | null): string {
    if (seconds == null || seconds <= 0) return '';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    const parts = [];
    if (d > 0) parts.push(d + 'd');
    if (h > 0 || d > 0) parts.push(h + 'h');
    if (m > 0 || h > 0 || d > 0) parts.push(m + 'm');
    parts.push(s + 's');
    return parts.join(' ') + ' remaining';
  }

  boostTargetSubtitle(target: PssRushTarget): string {
    const time = this.formatRemainingTime(target.remainingSeconds);
    if (target.targetType === 'room') {
      const room = this.details?.rooms.find(r => r.roomId === target.targetId);
      const current = room ? this.roomDesigns.get(room.roomDesignId) : null;
      const next = room?.upgradeRoomDesignId && room.upgradeRoomDesignId !== '0'
        ? this.roomDesigns.get(room.upgradeRoomDesignId)
        : null;
      const maybeLevel = this.extractLevelFromName(next?.name || current?.name || target.label);
      const base = maybeLevel ? ('Level ' + maybeLevel + ' upgrade in progress') : 'Upgrade in progress';
      return time ? base + ' · ' + time : base;
    }
    if (target.targetType === 'ship') {
      const base = 'Ship upgrade in progress';
      return time ? base + ' · ' + time : base;
    }
    const level = target.level || this.extractLevelFromName(target.label);
    const base = level ? ('Level ' + level + ' research in progress') : 'Research in progress';
    return time ? base + ' · ' + time : base;
  }

  boostTargetSprite(target: PssRushTarget): string {
    if (target.targetType === 'room') {
      const room = this.details?.rooms.find(r => r.roomId === target.targetId);
      const current = room ? this.roomDesigns.get(room.roomDesignId) : null;
      const next = room?.upgradeRoomDesignId && room.upgradeRoomDesignId !== '0'
        ? this.roomDesigns.get(room.upgradeRoomDesignId)
        : null;
      return spriteIdOrEmpty((next?.imageSpriteId || current?.imageSpriteId || '').trim());
    }
    return spriteIdOrEmpty((target.logoSpriteId || '').trim());
  }

  boostTargetTitle(target: PssRushTarget): string {
    if (target.targetType === 'room') return target.label;
    if (target.targetType === 'ship') {
      const design = this.shipDesigns.get(target.designId ?? '');
      return design ? ('→ ' + design.name) : 'Ship Upgrade';
    }
    const design = this.researchDesigns.get(target.designId ?? target.targetId);
    return design?.name || target.label;
  }

  private extractLevelFromName(name: string): string {
    const m = /(?:^|\s)(\d{1,2})\s*$/.exec(name);
    return m?.[1] ?? '';
  }

  applyBoostToSelected(): void {
    const target = this.selectedRushTarget;
    if (!target || this.boostApplying) return;

    this.boostApplying = true;
    this.boostError = '';
    this.boostResultMsg = '';

    this.auth.useBoost(target.targetType, target.targetId).subscribe({
      next: () => {
        this.showBoostModal = false;
        this.showBoostConfetti = true;
        this.boostResultMsg = target.targetType === 'research'
          ? 'Research boost applied successfully.'
          : target.targetType === 'ship'
            ? 'Ship boost applied successfully.'
            : 'Boost applied successfully.';
        setTimeout(() => { this.showBoostConfetti = false; }, 2500);
        if (this.details) {
          this.details.boostGauge = this.computeBoostGaugeAfterUse(target);
        }
        this.boostApplying = false;
      },
      error: (err) => {
        this.boostError = err?.message || 'Failed to apply boost';
        this.boostApplying = false;
      }
    });
  }

  private computeBoostGaugeAfterUse(target: PssRushTarget): PssBoostGaugeInfo {
    const current = this.details?.boostGauge;
    const currentAmount = current?.boostAmountSeconds ?? 0;
    const remainingSeconds = target.remainingSeconds ?? currentAmount;
    const amountUsed = Math.min(currentAmount, remainingSeconds);
    const newAmount = Math.max(0, currentAmount - amountUsed);
    const lastBoostDate = new Date(Date.now() - (newAmount * 1000)).toISOString();

    return {
      nextAvailableDate: current?.nextAvailableDate ?? '',
      isReady: newAmount >= 300,
      secondsUntilReady: Math.max(0, 300 - newAmount),
      boostAmountSeconds: newAmount,
      hasBoostAmount: newAmount > 0,
      boostAmountHhmm: this.formatBoostAmountHhMm(newAmount),
      lastBoostDate,
      source: current?.source ?? 'manual',
    };
  }

  private formatBoostAmountHhMm(totalSeconds: number): string {
    const safe = Math.max(0, totalSeconds | 0);
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  openSupportPage(): void {
    window.open(this.lastSupportUrl ?? 'https://buymeacoffee.com/lordtrist', '_blank');
  }

  createSupportClaimToken(): void {
    if (!this.session || this.supportLoading) return;
    this.supportLoading = true;
    this.supportError = '';
    this.supportSvc.createClaimToken(this.session.userId, this.session.name).subscribe({
      next: r => {
        this.supportClaimToken = r.claimToken;
        this.supportClaimExpiresAt = new Date(r.expiresAt);
        this.lastSupportUrl = r.supportUrl;
        this.supportLoading = false;
      },
      error: err => {
        this.supportError = err?.error?.error ?? 'Failed to create claim token';
        this.supportLoading = false;
      }
    });
  }

  get isAdmin(): boolean {
    return !!this.session?.isAdmin;
  }

  goToAdmin(): void {
    if (!this.isAdmin) return;
    this.router.navigate(['/admin']);
  }

  refreshSupportStatus(): void {
    if (!this.session || this.supportLoading) return;
    this.supportLoading = true;
    this.supportError = '';
    this.supportSvc.refreshStatus(this.session.userId).subscribe({
      next: r => {
        this.supportStatus = r.status;
        this.supportLoading = false;
      },
      error: err => {
        this.supportError = err?.error?.error ?? 'Failed to refresh support status';
        this.supportLoading = false;
      }
    });
  }

  copySupportClaimToken(): void {
    if (!this.supportClaimToken) return;
    navigator.clipboard.writeText(this.supportClaimToken).catch(() => {});
  }

  get deployedRooms(): PssRoom[] {
    return (this.details?.rooms ?? []).filter(r => r.status === 'Normal');
  }

  // ── Crew AI ────────────────────────────────────────────────────────────────

  selectCrew(c: PssCrewMember): void {
    this.selectedCrew = c;
    this.showCrewAI = false;
    this.aiNewConditionId = '';
    this.aiNewActionId = '';
    this.aiError = '';
  }

  closeCrew(): void {
    this.selectedCrew = null;
    this.showCrewAI = false;
    this.aiNewConditionId = '';
    this.aiNewActionId = '';
  }

  get selectedCrewAI(): PssCharacterAction[] {
    if (!this.selectedCrew || !this.details) return [];
    return this.details.characterActions
      .filter(a => a.characterId === this.selectedCrew!.characterId)
      .sort((a, b) => a.index - b.index);
  }

  get compareCrewTargets(): CompareTarget[] {
    if (!this.details) return [];
    return this.details.crew
      .filter(c => c.characterId !== this.selectedCrew?.characterId)
      .map(c => {
        const design = this.characterDesigns.get(c.designId);
        return {
          id: c.characterId,
          name: c.customName || design?.name || `#${c.designId}`,
          spriteId: spriteIdOrEmpty(design?.profileSpriteId),
          rules: this.details!.characterActions
            .filter(a => a.characterId === c.characterId)
            .sort((a, b) => a.index - b.index),
        };
      });
  }

  conditionTypeList(): PssConditionType[] {
    return Array.from(this.conditionTypes.values());
  }

  actionTypeList(): PssActionType[] {
    return Array.from(this.actionTypes.values());
  }

  addAIRule(conditionTypeId: string = this.aiNewConditionId, actionTypeId: string = this.aiNewActionId): void {
    if (!this.selectedCrew || this.aiSaving || !conditionTypeId || !actionTypeId) return;
    const nextIndex = this.selectedCrewAI.length
      ? Math.max(...this.selectedCrewAI.map(a => a.index)) + 1
      : 0;
    this.aiSaving = true;
    this.aiError = '';
    this.auth.createCharacterAction(
      this.selectedCrew.characterId,
      conditionTypeId,
      actionTypeId,
      nextIndex
    ).subscribe({
      next: resp => {
        const match = resp.xml.match(/CharacterActionId="(\d+)"[^/]*CharacterId="(\d+)"[^/]*CharacterActionIndex="(\d+)"[^/]*ConditionTypeId="(\d+)"[^/]*ActionTypeId="(\d+)"/);
        if (match && this.details) {
          this.details.characterActions.push({
            characterActionId: match[1],
            characterId:       match[2],
            index:             parseInt(match[3], 10),
            conditionTypeId:   match[4],
            actionTypeId:      match[5],
          });
        }
        this.aiNewConditionId = '';
        this.aiNewActionId = '';
        this.aiSaving = false;
      },
      error: err => {
        this.aiError = err?.error?.error || 'Failed to add rule';
        this.aiSaving = false;
      }
    });
  }

  handleCrewAiAdd(event: { conditionTypeId: string; actionTypeId: string }): void {
    this.addAIRule(event.conditionTypeId, event.actionTypeId);
  }

  deleteAllCrewAIRules(): void {
    if (!this.selectedCrew || this.aiSaving) return;
    this.aiSaving = true;
    this.aiError = '';
    const crewId = this.selectedCrew.characterId;
    this.auth.deleteAllCharacterActions(crewId).subscribe({
      next: () => {
        if (this.details) {
          this.details.characterActions = this.details.characterActions
            .filter(a => a.characterId !== crewId);
        }
        this.aiSaving = false;
      },
      error: err => {
        this.aiError = err?.error?.error || 'Failed to delete all rules';
        this.aiSaving = false;
      }
    });
  }

  deleteAllCompareCrewAIRules(targetId: string): void {
    if (!targetId || this.compareAiSaving) return;
    this.compareAiSaving = true;
    this.compareAiError = '';
    this.auth.deleteAllCharacterActions(targetId).subscribe({
      next: () => {
        if (this.details) {
          this.details.characterActions = this.details.characterActions
            .filter(a => a.characterId !== targetId);
        }
        this.compareAiSaving = false;
      },
      error: err => {
        this.compareAiError = err?.error?.error || 'Failed to delete all compare rules';
        this.compareAiSaving = false;
      }
    });
  }

  private compareCrewAI(crewId: string): PssCharacterAction[] {
    if (!this.details) return [];
    return this.details.characterActions
      .filter(a => a.characterId === crewId)
      .sort((a, b) => a.index - b.index);
  }

  handleCompareCrewAiAdd(event: { targetId: string; conditionTypeId: string; actionTypeId: string }): void {
    if (!this.details || this.compareAiSaving || !event.targetId) return;
    const nextIndex = this.compareCrewAI(event.targetId).length
      ? Math.max(...this.compareCrewAI(event.targetId).map(a => a.index)) + 1
      : 0;
    this.compareAiSaving = true;
    this.compareAiError = '';
    this.auth.createCharacterAction(event.targetId, event.conditionTypeId, event.actionTypeId, nextIndex).subscribe({
      next: resp => {
        const match = resp.xml.match(/CharacterActionId="(\d+)"[^/]*CharacterId="(\d+)"[^/]*CharacterActionIndex="(\d+)"[^/]*ConditionTypeId="(\d+)"[^/]*ActionTypeId="(\d+)"/);
        if (match && this.details) {
          this.details.characterActions.push({
            characterActionId: match[1],
            characterId: match[2],
            index: parseInt(match[3], 10),
            conditionTypeId: match[4],
            actionTypeId: match[5],
          });
        }
        this.compareAiSaving = false;
      },
      error: err => {
        this.compareAiError = err?.error?.error || 'Failed to add compare rule';
        this.compareAiSaving = false;
      }
    });
  }

  handleCompareCrewAiDelete(event: { targetId: string; rule: any }): void {
    if (!this.details || this.compareAiSaving || !event.rule) return;
    this.compareAiSaving = true;
    this.compareAiError = '';
    this.auth.deleteCharacterAction(event.rule.characterActionId).subscribe({
      next: () => {
        if (this.details) {
          this.details.characterActions = this.details.characterActions
            .filter(a => a.characterActionId !== event.rule.characterActionId);
        }
        this.compareAiSaving = false;
      },
      error: err => {
        this.compareAiError = err?.error?.error || 'Failed to delete compare rule';
        this.compareAiSaving = false;
      }
    });
  }

  handleCompareCrewAiMove(event: { targetId: string; rule: any; newIndex: number }): void {
    if (!this.details || this.compareAiSaving || !event.rule) return;
    this.compareAiSaving = true;
    this.compareAiError = '';
    this.auth.moveCharacterAction(event.rule.characterActionId, event.newIndex).subscribe({
      next: () => {
        if (this.details) {
          const arr = this.compareCrewAI(event.targetId);
          const fromPos = arr.findIndex(a => a.characterActionId === event.rule.characterActionId);
          if (fromPos !== -1) {
            const it = arr.splice(fromPos, 1)[0];
            arr.splice(Math.min(event.newIndex, arr.length), 0, it);
            arr.forEach((a, i) => { a.index = i; });
            // update global list indexes
            this.details.characterActions = this.details.characterActions
              .filter(a => a.characterId !== event.targetId)
              .concat(arr);
          }
        }
        this.compareAiSaving = false;
      },
      error: err => {
        this.compareAiError = err?.error?.error || 'Failed to reorder compare rules';
        this.compareAiSaving = false;
      }
    });
  }

  handleCompareCrewAiImport(event: { targetId: string; payload: AiImportPayload }): void {
    if (!this.details || this.compareAiSaving || !event.targetId || !event.payload.rules.length) return;
    const crewId = event.targetId;
    this.compareAiSaving = true;
    this.compareAiError = '';

    const addRulesSequentially = (startIndex: number) =>
      from(event.payload.rules).pipe(
        concatMap((rule, i) =>
          this.auth.createCharacterAction(crewId, rule.conditionTypeId, rule.actionTypeId, startIndex + i)
        ),
        tap(resp => {
          const re = /CharacterActionId="(\d+)"[^/]*CharacterId="(\d+)"[^/]*CharacterActionIndex="(\d+)"[^/]*ConditionTypeId="(\d+)"[^/]*ActionTypeId="(\d+)"/g;
          let m: RegExpExecArray | null;
          let last: RegExpExecArray | null = null;
          while ((m = re.exec(resp.xml)) !== null) { last = m; }
          if (last && this.details) {
            this.details.characterActions.push({
              characterActionId: last[1],
              characterId: last[2],
              index: parseInt(last[3], 10),
              conditionTypeId: last[4],
              actionTypeId: last[5],
            });
          }
        }),
        mapTo(undefined as void)
      );

    if (!event.payload.replaceAll) {
      const startIndex = this.compareCrewAI(crewId).length
        ? Math.max(...this.compareCrewAI(crewId).map(a => a.index)) + 1
        : 0;
      addRulesSequentially(startIndex).subscribe({
        error: err => { this.compareAiError = err?.error?.error || 'Compare import failed'; this.compareAiSaving = false; },
        complete: () => { this.compareAiSaving = false; },
      });
      return;
    }

    const delete$: Observable<{ xml: string } | null> = this.compareCrewAI(crewId).length
      ? this.auth.deleteAllCharacterActions(crewId)
      : of(null);

    delete$.subscribe({
      next: () => {
        if (this.details) {
          this.details.characterActions = this.details.characterActions
            .filter(a => a.characterId !== crewId);
        }
        addRulesSequentially(0).subscribe({
          error: err => { this.compareAiError = err?.error?.error || err?.message || 'Compare import failed'; this.compareAiSaving = false; },
          complete: () => { this.compareAiSaving = false; },
        });
      },
      error: (err: any) => {
        this.compareAiError = err?.error?.error || err?.message || 'Compare import failed';
        this.compareAiSaving = false;
      },
      complete: () => {
        // noop, operation finishes in inner sequence
      }
    });
  }

  handleCrewAiCopyFromCompare(targetId: string): void {
    if (!this.details || !this.selectedCrew || !targetId) return;
    const sourceRules = this.compareCrewAI(targetId).map(r => ({ conditionTypeId: r.conditionTypeId, actionTypeId: r.actionTypeId }));
    if (!sourceRules.length) return;
    this.importCrewAIRules({ rules: sourceRules, replaceAll: true });
  }

  importCrewAIRules(payload: AiImportPayload): void {
    const rules = payload.rules;
    if (!this.selectedCrew || this.aiSaving || !rules.length) return;
    const crewId = this.selectedCrew.characterId;
    this.aiSaving = true;
    this.aiError = '';

    // Helper: add rules one at a time, strictly in order.
    const addRulesSequentially = (startIndex: number): Observable<void> =>
      from(rules).pipe(
        concatMap((rule, i) =>
          this.auth.createCharacterAction(crewId, rule.conditionTypeId, rule.actionTypeId, startIndex + i)
        ),
        tap(resp => {
          // Parse only the newly created action — last CharacterAction in the response.
          const re = /CharacterActionId="(\d+)"[^/]*CharacterId="(\d+)"[^/]*CharacterActionIndex="(\d+)"[^/]*ConditionTypeId="(\d+)"[^/]*ActionTypeId="(\d+)"/g;
          let m: RegExpExecArray | null;
          let last: RegExpExecArray | null = null;
          while ((m = re.exec(resp.xml)) !== null) { last = m; }
          if (last && this.details) {
            this.details.characterActions.push({
              characterActionId: last[1],
              characterId:       last[2],
              index:             parseInt(last[3], 10),
              conditionTypeId:   last[4],
              actionTypeId:      last[5],
            });
          }
        }),
        mapTo(undefined as void)
      );

    if (!payload.replaceAll) {
      // ── Append mode: just add sequentially after existing rules ─────────
      const startIndex = this.selectedCrewAI.length
        ? Math.max(...this.selectedCrewAI.map(a => a.index)) + 1
        : 0;
      addRulesSequentially(startIndex).subscribe({
        error: err => { this.aiError = err?.error?.error || 'Import failed'; this.aiSaving = false; },
        complete: () => { this.aiSaving = false; },
      });
      return;
    }

    // ── Replace mode: bulk delete all (if any exist) → add sequentially from index 0 ──
    const delete$: Observable<unknown> = this.selectedCrewAI.length
      ? this.auth.deleteAllCharacterActions(crewId)
      : of(null);

    delete$.pipe(
      tap(() => {
        if (this.details) {
          this.details.characterActions = this.details.characterActions
            .filter(a => a.characterId !== crewId);
        }
      }),
      switchMap(() => addRulesSequentially(0)),
    ).subscribe({
      error: err => { this.aiError = err?.error?.error || err?.message || 'Import failed'; this.aiSaving = false; },
      complete: () => { this.aiSaving = false; },
    });
  }

  deleteAIRule(action: PssCharacterAction): void {
    this.aiDeleteQueue.push(action);
    if (!this.aiDeleteInProgress) {
      this.processAiDeleteQueue();
    }
  }

  private processAiDeleteQueue(): void {
    const action = this.aiDeleteQueue.shift();
    if (!action) {
      this.aiDeleteInProgress = false;
      this.aiSaving = false;
      return;
    }

    this.aiDeleteInProgress = true;
    this.aiSaving = true;
    this.aiError = '';

    this.auth.deleteCharacterAction(action.characterActionId).subscribe({
      next: () => {
        if (this.details) {
          this.details.characterActions = this.details.characterActions
            .filter(a => a.characterActionId !== action.characterActionId);
        }
        this.aiSaving = false;
        this.processAiDeleteQueue();
      },
      error: err => {
        this.aiError = err?.error?.error || 'Failed to delete rule';
        this.aiSaving = false;
        this.processAiDeleteQueue();
      }
    });
  }

  moveCrewAIRule(action: PssCharacterAction, newIndex: number): void {
    if (this.aiSaving) return;
    this.aiSaving = true;
    this.aiError = '';
    this.auth.moveCharacterAction(action.characterActionId, newIndex).subscribe({
      next: () => {
        if (this.details) {
          const arr = this.details.characterActions
            .filter(a => a.characterId === action.characterId)
            .sort((a, b) => a.index - b.index);
          const fromPos = arr.findIndex(a => a.characterActionId === action.characterActionId);
          if (fromPos !== -1) {
            const [moved] = arr.splice(fromPos, 1);
            arr.splice(Math.min(newIndex, arr.length), 0, moved);
            arr.forEach((a, i) => { a.index = i; });
          }
        }
        this.aiSaving = false;
      },
      error: err => {
        this.aiError = err?.error?.error || 'Failed to reorder rules';
        this.aiSaving = false;
      }
    });
  }

  handleCrewAiEdit(event: { rule: any; conditionTypeId: string; actionTypeId: string }): void {
    if (!this.selectedCrew || this.aiSaving || !event.rule) return;
    this.aiSaving = true;
    this.aiError = '';
    this.auth.editCharacterAction(event.rule.characterActionId, event.conditionTypeId, event.actionTypeId).subscribe({
      next: () => {
        if (this.details) {
          const target = this.details.characterActions.find(a => a.characterActionId === event.rule.characterActionId);
          if (target) {
            target.conditionTypeId = event.conditionTypeId;
            target.actionTypeId = event.actionTypeId;
          }
        }
        this.aiSaving = false;
      },
      error: err => {
        this.aiError = err?.error?.error || 'Failed to edit rule';
        this.aiSaving = false;
      }
    });
  }

  handleCompareCrewAiEdit(event: { targetId: string; rule: any; conditionTypeId: string; actionTypeId: string }): void {
    if (!this.details || this.compareAiSaving || !event.rule || !event.targetId) return;
    this.compareAiSaving = true;
    this.compareAiError = '';
    this.auth.editCharacterAction(event.rule.characterActionId, event.conditionTypeId, event.actionTypeId).subscribe({
      next: () => {
        const target = this.details?.characterActions.find(a => a.characterActionId === event.rule.characterActionId);
        if (target) {
          target.conditionTypeId = event.conditionTypeId;
          target.actionTypeId = event.actionTypeId;
        }
        this.compareAiSaving = false;
      },
      error: err => {
        this.compareAiError = err?.error?.error || 'Failed to edit compare rule';
        this.compareAiSaving = false;
      }
    });
  }

  // ── Inventory / Use-Item modal ─────────────────────────────────────────────

  get groupedUseItemResults(): { rewardDesignId: string; totalQty: number }[] {
    const map = new Map<string, number>();
    for (const r of this.useItemResults) {
      map.set(r.rewardDesignId, (map.get(r.rewardDesignId) ?? 0) + r.rewardQty);
    }
    return Array.from(map.entries()).map(([rewardDesignId, totalQty]) => ({ rewardDesignId, totalQty }));
  }

  openUseItemModal(item: PssInventoryItem, event?: Event): void {
    if (this.featureFlags.isRestrictedMode()) {
      return;
    }
    event?.stopPropagation();
    this.useItemModal = item;
    this.useItemCount = 1;
    this.useItemProgress = 0;
    this.useItemTotal = 0;
    this.useItemRunning = false;
    this.useItemDone = false;
    this.useItemError = '';
    this.useItemRetryMsg = '';
    this.useItemRetryCountdown = 0;
    if (this.useItemRetryTimer) { clearTimeout(this.useItemRetryTimer); this.useItemRetryTimer = null; }
    this.useItemResults = [];
    this.useItemResultsGrouped = true;
    this.useItemSelectedReward = null;
    this.useItemFireCommon = false;
    this.useItemFireElite = false;
    this.useItemFiredIds = new Set<string>();
    this.useItemCharacters = [];
    this.useItemQueue = [];

    const freeCount = item.freeInstances?.reduce((sum, inst) => sum + (inst.qty || 0), 0) ?? 0;
    if (freeCount === 0) {
      this.useItemError = 'No available instances to use. Please select another item.';
    }
  }

  closeUseItemModal(): void {
    if (this.useItemRunning) return;
    this.useItemSelectedReward = null;
    this.useItemModal = null;
  }

  useItemMaxCount(): number {
    if (!this.useItemModal) return 0;
    return this.useItemModal.freeInstances.reduce((s, f) => s + f.qty, 0);
  }

  /** True when the open item is a crew lottery that supports auto-firing 1-2 star recruits. */
  useItemSupportsFiring(): boolean {
    if (!this.useItemModal) return false;
    const name = this.itemDesigns.get(this.useItemModal.designId)?.name ?? '';
    return DashboardComponent.FIREABLE_LOTTERY_ITEM_NAMES.has(name.trim().toLowerCase());
  }

  /**
   * True only when the recruit is provably a fresh draw (IsNew="true" in the
   * activation response — never an existing crew member echoed back) AND its
   * design rarity is positively known AND that tier's fire toggle is on.
   * Deleting crew is irreversible, so any uncertainty (designs not loaded,
   * unknown designId, missing/blank rarity, missing IsNew) must resolve to
   * "do not fire".
   */
  private shouldFireRecruit(character: PssCrewMember): boolean {
    if (character.isNew !== true) return false;
    const rarity = this.characterDesigns.get(character.designId)?.rarity?.trim().toLowerCase();
    return (rarity === 'common' && this.useItemFireCommon)
        || (rarity === 'elite' && this.useItemFireElite);
  }

  startUseItems(): void {
    if (this.featureFlags.isRestrictedMode()) {
      return;
    }
    const item = this.useItemModal;
    if (!item || this.useItemRunning) return;

    const count = Math.max(1, Math.min(this.useItemCount, this.useItemMaxCount()));

    this.useItemQueue = [];
    let remaining = count;
    for (const inst of item.freeInstances) {
      const instanceId = inst.id?.trim();
      if (!instanceId) continue;
      for (let i = 0; i < inst.qty && remaining > 0; i++, remaining--) {
        this.useItemQueue.push(instanceId);
      }
      if (remaining <= 0) break;
    }

    if (this.useItemQueue.length === 0) {
      this.useItemError = 'No valid item instances are selected. Please choose an item with available instances.';
      this.useItemRunning = false;
      this.useItemDone = false;
      return;
    }

    this.useItemTotal = this.useItemQueue.length;
    this.useItemProgress = 0;
    this.useItemResults = [];
    this.useItemRunning = true;
    this.useItemDone = false;
    this.useItemError = '';
    this.useNextItem();
  }

  private useNextItem(): void {
    let itemId = '';
    while (this.useItemQueue.length > 0 && !itemId) {
      itemId = (this.useItemQueue.shift() || '').trim();
    }
    if (!itemId) {
      this.useItemRunning = false;
      this.useItemDone = true;
      if (this.useItemProgress === 0) {
        this.useItemError = 'Item ID is missing. Please ensure an item is selected before using it.';
      }
      return;
    }
    this.auth.activateItem(itemId).subscribe({
      next: result => {
        this.useItemProgress++;

        if (result.rewardDesignId) {
          this.useItemResults.push({ rewardDesignId: result.rewardDesignId, rewardQty: result.rewardQty });
          this.invNewItems.add(result.rewardDesignId);

          if (this.details) {
            const inv = this.details.inventory;
            // Decrement used item
            const usedIdx = inv.findIndex(i => i.designId === this.useItemModal?.designId);
            if (usedIdx >= 0) {
              const u = inv[usedIdx];
              if (u.quantity - 1 <= 0) {
                inv.splice(usedIdx, 1);
              } else {
                inv[usedIdx] = { ...u, quantity: u.quantity - 1 };
              }
            }
            // Update or add reward item
            const rewardIdx = inv.findIndex(i => i.designId === result.rewardDesignId);
            if (rewardIdx >= 0) {
              const r = inv[rewardIdx];
              const newQty = result.rewardNewTotal ?? (r.quantity + result.rewardQty);
              inv[rewardIdx] = { ...r, quantity: newQty };
            } else {
              inv.push({ designId: result.rewardDesignId, quantity: result.rewardNewTotal ?? result.rewardQty, equippedBy: [], freeInstances: [] });
            }
            this.details = { ...this.details, inventory: [...inv] };
          }
        }

        // 600ms delay between activations to avoid rate-limiting
        const scheduleNext = () => {
          this.useItemRetryTimer = setTimeout(() => this.useNextItem(), 600);
        };

        if (result.character) {
          const character = result.character;
          this.useItemCharacters.push(character);
          const addToCrew = () => {
            if (this.details && !this.details.crew.some(c => c.characterId === character.characterId)) {
              this.details.crew = [...this.details.crew, character];
            }
          };

          if (this.shouldFireRecruit(character)) {
            // Delete before scheduling the next activation so PSS calls stay sequential.
            this.auth.deleteCharacter(character.characterId).subscribe({
              next: () => {
                const fired = new Set(this.useItemFiredIds);
                fired.add(character.characterId);
                this.useItemFiredIds = fired;
                scheduleNext();
              },
              error: err => {
                console.warn('Failed to fire crew', character.characterId, err?.message ?? err);
                addToCrew();
                scheduleNext();
              }
            });
            return;
          }
          addToCrew();
        }

        scheduleNext();
      },
      error: err => {
        const msg: string = err?.error?.error ?? err?.message ?? 'Failed to activate item';
        const lower = msg.toLowerCase();

        if (lower.includes('access') && (lower.includes('invalid') || lower.includes('expired') || lower.includes('unauthori'))) {
          this.useItemRetryMsg = 'Session expired \u2014 refreshing token\u2026';
          this.auth.reauthenticate().subscribe({
            next: () => {
              this.useItemRetryMsg = '';
              this.useItemQueue.unshift(itemId);
              this.useItemRetryTimer = setTimeout(() => this.useNextItem(), 600);
            },
            error: () => {
              this.useItemRetryMsg = '';
              this.useItemError = 'Session expired \u2014 please log in again';
              this.useItemRunning = false;
            }
          });
        } else if (lower.includes('too many') || lower.includes('toomany') || lower.includes('rate limit')) {
          this.useItemQueue.unshift(itemId);
          this.startRetryCountdown(30, 'Too many requests \u2014 retrying in', () => this.useNextItem());
        } else if (lower.includes('timeout') || lower.includes('network') || lower.includes('econnreset') || lower.includes('failed to fetch') || lower.includes('etimedout')) {
          this.useItemQueue.unshift(itemId);
          this.startRetryCountdown(30, 'Server slow / timeout \u2014 retrying in', () => this.useNextItem());
        } else {
          this.useItemError = msg;
          this.useItemRunning = false;
        }
      }
    });
  }

  private startRetryCountdown(seconds: number, prefix: string, callback: () => void): void {
    this.useItemRetryCountdown = seconds;
    this.useItemRetryMsg = prefix + ' ' + seconds + 's\u2026';
    const tick = () => {
      this.useItemRetryCountdown--;
      if (this.useItemRetryCountdown <= 0) {
        this.useItemRetryMsg = '';
        this.useItemRetryCountdown = 0;
        callback();
      } else {
        this.useItemRetryMsg = prefix + ' ' + this.useItemRetryCountdown + 's\u2026';
        this.useItemRetryTimer = setTimeout(tick, 1000);
      }
    };
    this.useItemRetryTimer = setTimeout(tick, 1000);
  }

  // ── Room AI ────────────────────────────────────────────────────────────────

  roomSupportsAI(roomDesignId: string): boolean {
    const flags = this.roomDesigns.get(roomDesignId)?.flags ?? 0;
    return (flags & 1) !== 0;
  }

  roomHasStatBoost(roomDesignId: string): boolean {
    const design = this.roomDesigns.get(roomDesignId);
    if (!design) return false;
    const et = design.enhancementType;
    if (et && et !== 'None' && et !== '') return true;
    return design.roomType in DashboardComponent.ROOM_TYPE_STAT;
  }

  selectRoom(r: PssRoom): void {
    if (!this.roomSupportsAI(r.roomDesignId) && !this.roomHasStatBoost(r.roomDesignId)) return;
    this.selectedRoom = r;
    this.selectedAmmoId = null;
    this.roomAiNewConditionId = '';
    this.roomAiNewActionId = '';
    this.roomAiError = '';
  }

  closeRoom(): void {
    this.selectedRoom = null;
    this.roomAiNewConditionId = '';
    this.roomAiNewActionId = '';
  }

  get selectedRoomAI(): PssRoomAction[] {
    if (!this.selectedRoom || !this.details) return [];
    return this.details.roomActions
      .filter(a => a.roomId === this.selectedRoom!.roomId)
      .sort((a, b) => a.index - b.index);
  }

  addRoomAIRule(conditionTypeId: string = this.roomAiNewConditionId, actionTypeId: string = this.roomAiNewActionId): void {
    if (!this.selectedRoom || this.roomAiSaving || !conditionTypeId || !actionTypeId) return;
    const nextIndex = this.selectedRoomAI.length
      ? Math.max(...this.selectedRoomAI.map(a => a.index)) + 1
      : 0;
    this.roomAiSaving = true;
    this.roomAiError = '';
    this.auth.createRoomAction(
      this.selectedRoom.roomId,
      conditionTypeId,
      actionTypeId,
      nextIndex
    ).subscribe({
      next: resp => {
        const match = resp.xml.match(/RoomActionId="(\d+)"[^/]*RoomId="(\d+)"[^/]*RoomActionIndex="(\d+)"[^/]*ConditionTypeId="(\d+)"[^/]*ActionTypeId="(\d+)"/);
        if (match && this.details) {
          this.details.roomActions.push({
            roomActionId:    match[1],
            roomId:          match[2],
            index:           parseInt(match[3], 10),
            conditionTypeId: match[4],
            actionTypeId:    match[5],
          });
        }
        this.roomAiNewConditionId = '';
        this.roomAiNewActionId = '';
        this.roomAiSaving = false;
      },
      error: err => {
        this.roomAiError = err?.error?.error || 'Failed to add rule';
        this.roomAiSaving = false;
      }
    });
  }

  handleRoomAiAdd(event: { conditionTypeId: string; actionTypeId: string }): void {
    this.addRoomAIRule(event.conditionTypeId, event.actionTypeId);
  }

  importRoomAIRules(payload: AiImportPayload): void {
    const rules = payload.rules;
    if (!this.selectedRoom || this.roomAiSaving || !rules.length) return;
    const roomId = this.selectedRoom.roomId;
    this.roomAiSaving = true;
    this.roomAiError = '';

    // Helper: add rules one at a time, strictly in order.
    const addRulesSequentially = (startIndex: number): Observable<void> =>
      from(rules).pipe(
        concatMap((rule, i) =>
          this.auth.createRoomAction(roomId, rule.conditionTypeId, rule.actionTypeId, startIndex + i)
        ),
        tap(resp => {
          // Parse only the newly created action — last RoomAction in the response.
          const re = /RoomActionId="(\d+)"[^/]*RoomId="(\d+)"[^/]*RoomActionIndex="(\d+)"[^/]*ConditionTypeId="(\d+)"[^/]*ActionTypeId="(\d+)"/g;
          let m: RegExpExecArray | null;
          let last: RegExpExecArray | null = null;
          while ((m = re.exec(resp.xml)) !== null) { last = m; }
          if (last && this.details) {
            this.details.roomActions.push({
              roomActionId:    last[1],
              roomId:          last[2],
              index:           parseInt(last[3], 10),
              conditionTypeId: last[4],
              actionTypeId:    last[5],
            });
          }
        }),
        mapTo(undefined as void)
      );

    if (!payload.replaceAll) {
      // ── Append mode: just add sequentially after existing rules ─────────
      const startIndex = this.selectedRoomAI.length
        ? Math.max(...this.selectedRoomAI.map(a => a.index)) + 1
        : 0;
      addRulesSequentially(startIndex).subscribe({
        error: err => { this.roomAiError = err?.error?.error || 'Import failed'; this.roomAiSaving = false; },
        complete: () => { this.roomAiSaving = false; },
      });
      return;
    }

    // ── Replace mode: bulk delete all (if any exist) → add sequentially from index 0 ──
    const deleteRoom$: Observable<unknown> = this.selectedRoomAI.length
      ? this.auth.deleteAllRoomActions(roomId)
      : of(null);

    deleteRoom$.pipe(
      tap(() => {
        if (this.details) {
          this.details.roomActions = this.details.roomActions
            .filter(a => a.roomId !== roomId);
        }
      }),
      switchMap(() => addRulesSequentially(0)),
    ).subscribe({
      error: err => { this.roomAiError = err?.error?.error || err?.message || 'Import failed'; this.roomAiSaving = false; },
      complete: () => { this.roomAiSaving = false; },
    });
  }

  deleteAllRoomAIRules(): void {
    if (!this.selectedRoom || this.roomAiSaving) return;
    this.roomAiSaving = true;
    this.roomAiError = '';
    const roomId = this.selectedRoom.roomId;
    this.auth.deleteAllRoomActions(roomId).subscribe({
      next: () => {
        if (this.details) {
          this.details.roomActions = this.details.roomActions
            .filter(a => a.roomId !== roomId);
        }
        this.roomAiSaving = false;
      },
      error: err => {
        this.roomAiError = err?.error?.error || 'Failed to delete all rules';
        this.roomAiSaving = false;
      }
    });
  }

  deleteRoomAIRule(action: PssRoomAction): void {
    this.roomAiDeleteQueue.push(action);
    if (!this.roomAiDeleteInProgress) {
      this.processRoomAiDeleteQueue();
    }
  }

  private processRoomAiDeleteQueue(): void {
    const action = this.roomAiDeleteQueue.shift();
    if (!action) {
      this.roomAiDeleteInProgress = false;
      this.roomAiSaving = false;
      return;
    }

    this.roomAiDeleteInProgress = true;
    this.roomAiSaving = true;
    this.roomAiError = '';

    this.auth.deleteRoomAction(action.roomActionId).subscribe({
      next: () => {
        if (this.details) {
          this.details.roomActions = this.details.roomActions
            .filter(a => a.roomActionId !== action.roomActionId);
        }
        this.roomAiSaving = false;
        this.processRoomAiDeleteQueue();
      },
      error: err => {
        this.roomAiError = err?.error?.error || 'Failed to delete rule';
        this.roomAiSaving = false;
        this.processRoomAiDeleteQueue();
      }
    });
  }

  moveRoomAIRule(action: PssRoomAction, newIndex: number): void {
    if (this.roomAiSaving) return;
    this.roomAiSaving = true;
    this.roomAiError = '';
    this.auth.moveRoomAction(action.roomActionId, newIndex).subscribe({
      next: () => {
        if (this.details) {
          const arr = this.details.roomActions
            .filter(a => a.roomId === action.roomId)
            .sort((a, b) => a.index - b.index);
          const fromPos = arr.findIndex(a => a.roomActionId === action.roomActionId);
          if (fromPos !== -1) {
            const [moved] = arr.splice(fromPos, 1);
            arr.splice(Math.min(newIndex, arr.length), 0, moved);
            arr.forEach((a, i) => { a.index = i; });
          }
        }
        this.roomAiSaving = false;
      },
      error: err => {
        this.roomAiError = err?.error?.error || 'Failed to reorder rules';
        this.roomAiSaving = false;
      }
    });
  }

  handleRoomAiEdit(event: { rule: any; conditionTypeId: string; actionTypeId: string }): void {
    if (!this.selectedRoom || this.roomAiSaving || !event.rule) return;
    this.roomAiSaving = true;
    this.roomAiError = '';
    this.auth.editRoomAction(event.rule.roomActionId, event.conditionTypeId, event.actionTypeId).subscribe({
      next: () => {
        if (this.details) {
          const target = this.details.roomActions.find(a => a.roomActionId === event.rule.roomActionId);
          if (target) {
            target.conditionTypeId = event.conditionTypeId;
            target.actionTypeId = event.actionTypeId;
          }
        }
        this.roomAiSaving = false;
      },
      error: err => {
        this.roomAiError = err?.error?.error || 'Failed to edit rule';
        this.roomAiSaving = false;
      }
    });
  }

  // ── Selected-room combat stats ─────────────────────────────────────────────

  // Maps PSS room type to the PssCrewMember stat field that boosts that room.
  private static readonly ROOM_TYPE_STAT: Partial<Record<string, keyof PssCrewMember>> = {
    'Laser':      'weaponImp',
    'Cannon':     'weaponImp',
    'AntiCraft':  'weaponImp',
    'EMP':        'weaponImp',
    'Shield':     'scienceImp',
    'Teleport':   'scienceImp',
    'Cloak':      'scienceImp',
    'Radar':      'scienceImp',
    'Research':   'scienceImp',
    'Laboratory': 'scienceImp',
    'Missile':    'engineImp',
    'Engine':     'engineImp',
    'Android':    'engineImp',
    'Hangar':     'pilotImp',
    'Bridge':     'pilotImp',
    'Command':    'pilotImp',
    'Security':   'attackImp',
    'Medical':    'repairImp',
  };

  private static readonly STAT_LABELS: Partial<Record<string, string>> = {
    weaponImp:  'Wpn',
    engineImp:  'Eng',
    scienceImp: 'Sci',
    repairImp:  'Repair',
    pilotImp:   'Pilot',
    attackImp:  'Atk',
    hpImp:      'HP',
    abilityImp: 'Ability',
  };

  private static readonly ENHANCEMENT_TYPE_STAT: Record<string, keyof PssCrewMember> = {
    'Weapon':  'weaponImp',
    'Engine':  'engineImp',
    'Science': 'scienceImp',
    'Pilot':   'pilotImp',
    'Repair':  'repairImp',
    'Attack':  'attackImp',
  };

  get selectedRoomDesign(): PssRoomDesign | null {
    if (!this.selectedRoom) return null;
    return this.roomDesigns.get(this.selectedRoom.roomDesignId) ?? null;
  }

  get selectedRoomStatKey(): keyof PssCrewMember {
    const design = this.selectedRoomDesign;
    if (!design) return 'weaponImp';
    const etKey = DashboardComponent.ENHANCEMENT_TYPE_STAT[design.enhancementType];
    if (etKey) return etKey;
    return DashboardComponent.ROOM_TYPE_STAT[design.roomType] ?? 'weaponImp';
  }

  get selectedRoomStatLabel(): string {
    return DashboardComponent.STAT_LABELS[this.selectedRoomStatKey as string] ?? 'Stat';
  }

  get selectedRoomCrew(): PssCrewMember[] {
    if (!this.selectedRoom || !this.details) return [];
    const rid = this.selectedRoom.roomId;
    if (!rid || rid === '0') return [];
    return this.details.crew.filter(c => c.roomId === rid);
  }

  private static readonly CREW_STAT_KEYS: Array<keyof PssCrewMember> = [
    'weaponImp', 'attackImp', 'scienceImp', 'engineImp', 'pilotImp', 'repairImp', 'abilityImp', 'hpImp',
  ];

  private static readonly CREW_STAT_KEY_TO_ITEM_TYPE: Partial<Record<keyof PssCrewMember, string>> = {
    weaponImp: 'Weapon',
    attackImp: 'Attack',
    scienceImp: 'Science',
    engineImp: 'Engine',
    pilotImp: 'Pilot',
    repairImp: 'Repair',
    abilityImp: 'Ability',
    hpImp: 'HP',
  };

  private parseItemBonusValue(raw: string | undefined): number {
    if (!raw) return 0;
    const match = raw.trim().match(/[-+]?\d+(?:\.\d+)?/);
    if (!match) return 0;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private getCrewGearBoost(c: PssCrewMember, key: keyof PssCrewMember): number {
    const requiredType = DashboardComponent.CREW_STAT_KEY_TO_ITEM_TYPE[key];
    if (!requiredType) return 0;

    const requiredLower = requiredType.toLowerCase();
    let total = 0;

    const addDesignBonuses = (item: PssItemDesign | undefined): number => {
      if (!item) return 0;
      let v = 0;
      if ((item.enhancementType || '').trim().toLowerCase() === requiredLower) {
        v += this.parseItemBonusValue(item.enhancementValue || item.modifier);
      }
      if ((item.enhancementType2 || '').trim().toLowerCase() === requiredLower) {
        v += this.parseItemBonusValue(item.enhancementValue2 || item.modifier2);
      }
      if ((item.bonusEnhancementType || '').trim().toLowerCase() === requiredLower) {
        v += this.parseItemBonusValue(item.bonusEnhancementValue || '');
      }
      if ((item.bonusEnhancementType2 || '').trim().toLowerCase() === requiredLower) {
        v += this.parseItemBonusValue(item.bonusEnhancementValue2 || '');
      }
      return v;
    };

    if (c.equipItemIds && c.equipItemIds.length > 0 && this.details) {
      const itemIdSet = new Set(c.equipItemIds);
      for (const instanceId of itemIdSet) {
        const invItem = this.details.inventory.find(inv => inv.equippedItemIds?.includes(instanceId));
        if (!invItem) continue;
        total += addDesignBonuses(this.itemDesigns.get(invItem.designId));
        for (const bonus of invItem.instanceBonuses?.filter(b => b.itemId === instanceId) ?? []) {
          if ((bonus.enhancementType || '').trim().toLowerCase() === requiredLower) {
            total += this.parseItemBonusValue(bonus.enhancementValue || '');
          }
          if ((bonus.enhancementType2 || '').trim().toLowerCase() === requiredLower) {
            total += this.parseItemBonusValue(bonus.enhancementValue2 || '');
          }
        }
      }
      return total;
    }

    if (c.equipDesignIds && c.equipDesignIds.length > 0) {
      for (const equipId of c.equipDesignIds) {
        total += addDesignBonuses(this.itemDesigns.get(equipId));
      }
    }

    return total;
  }

  private static readonly CREW_STAT_KEY_TO_CHARACTER_FIELD: Partial<Record<keyof PssCrewMember, keyof PssCharacterDesign>> = {
    weaponImp: 'finalWeapon',
    attackImp: 'finalAttack',
    scienceImp: 'finalScience',
    engineImp: 'finalEngine',
    pilotImp: 'finalPilot',
    repairImp: 'finalRepair',
    abilityImp: 'specialAbilityArg',
    hpImp: 'finalHp',
  };

  private crewStatValue(c: PssCrewMember, key: keyof PssCrewMember): number {
    const training = parseInt((c[key] as string) || '0', 10) || 0;
    const gear = this.getCrewGearBoost(c, key);

    const charDesign = this.characterDesigns.get(c.designId);
    const charStatKey = DashboardComponent.CREW_STAT_KEY_TO_CHARACTER_FIELD[key];
    const baseStat = charDesign && charStatKey ? parseFloat((charDesign[charStatKey] as string) || '0') : 0;

    const baseWithTraining = baseStat * (1 + training / 100); // base * (1 + training%)
    let rawValue = baseWithTraining + gear;
    if (key === 'abilityImp') {
      rawValue = baseWithTraining * (1 + gear / 100);
    }
    if (rawValue > 0) return rawValue;

    // Fallback for no base stat case.
    if (gear !== 0) return gear;
    if (training !== 0) return baseStat * (1 + training / 100);

    for (const fallbackKey of DashboardComponent.CREW_STAT_KEYS) {
      if (fallbackKey === key) continue;
      const fallbackTraining = parseInt((c[fallbackKey] as string) || '0', 10) || 0;
      if (fallbackTraining !== 0) return fallbackTraining;
    }
    return 0;
  }

  get selectedRoomCrewBoost(): number {
    const key = this.selectedRoomStatKey;
    return this.selectedRoomCrew
      .reduce((sum, c) => sum + this.crewStatValue(c, key), 0);
  }

  private roomsTouching(
    a: PssRoom, ad: PssRoomDesign,
    b: PssRoom, bd: PssRoomDesign,
  ): boolean {
    const aRows = parseInt(ad.rows, 10) || 1, aCols = parseInt(ad.cols, 10) || 1;
    const bRows = parseInt(bd.rows, 10) || 1, bCols = parseInt(bd.cols, 10) || 1;
    const aT = a.row, aB = a.row + aRows, aL = a.col, aR = a.col + aCols;
    const bT = b.row, bB = b.row + bRows, bL = b.col, bR = b.col + bCols;
    const overlapH = bL < aR && bR > aL;
    const overlapV = bT < aB && bB > aT;
    const adjH = bR === aL || bL === aR;
    const adjV = bB === aT || bT === aB;
    return (overlapH && adjV) || (overlapV && adjH);
  }

  private get shipAbRooms(): PssRoom[] {
    if (!this.details) return [];
    return this.details.rooms.filter(r => {
      const type = (this.roomDesigns.get(r.roomDesignId)?.roomType ?? '').toLowerCase();
      return type.includes('boost') || type.includes('ability');
    });
  }

  get selectedRoomAdjacentAB(): PssRoom | null {
    if (!this.selectedRoom) return null;
    const selDesign = this.roomDesigns.get(this.selectedRoom.roomDesignId);
    if (!selDesign) return null;
    return this.shipAbRooms.find(ab => {
      const abDesign = this.roomDesigns.get(ab.roomDesignId);
      return abDesign && this.roomsTouching(this.selectedRoom!, selDesign, ab, abDesign);
    }) ?? null;
  }

  get selectedRoomAbBoost(): number {
    const ab = this.selectedRoomAdjacentAB;
    if (!ab) return 0;
    return parseInt(this.roomDesigns.get(ab.roomDesignId)?.capacity ?? '0', 10);
  }

  /**
   * What the selected room can be told to load or build, in the order the AI's
   * "Set Item {N}" actions index into. Distinct from `availableAmmo`, which
   * covers rooms whose ammo is hard-wired by MissileDesignId.
   */
  get selectedRoomAiItems(): PssAmmoDesign[] {
    const design = this.selectedRoomDesign;
    return design ? PssAuthService.itemsForRoom(design, this.ammoDesigns) : [];
  }

  get availableAmmo(): PssAmmoDesign[] {
    const design = this.selectedRoomDesign;
    if (!design || !design.missileDesignId || design.missileDesignId === '0') return [];
    return Array.from(this.ammoDesigns.values())
      .filter(a => a.missileDesignId === design.missileDesignId);
  }

  get selectedRoomFireRate(): number | null {
    const design = this.selectedRoomDesign;
    if (!design) return null;
    const baseReloadFrames = parseFloat(design.reloadTime);
    if (!baseReloadFrames || baseReloadFrames <= 0) return null;

    const baseReload = baseReloadFrames / 40; // convert frames to seconds (40 fps)

    let ammoMod = 100;
    if (this.selectedAmmoId) {
      const ammo = this.ammoDesigns.get(this.selectedAmmoId);
      if (ammo) ammoMod = parseInt(ammo.reloadModifier, 10) || 100;
    }

    const crewBoost  = this.selectedRoomCrewBoost;
    const abPresent  = this.selectedRoomAdjacentAB !== null;
    const abBoost    = this.selectedRoomAbBoost;
    const ammoFactor = ammoMod * 0.01;
    const crewFactor = (crewBoost * 0.01) + 1;
    const abFactor   = abPresent ? ((((abBoost * 0.01) + 1) * 15) * 0.01) + 1 : 1;
    return (baseReload / ammoFactor) / (crewFactor * abFactor);
  }

  get selectedRoomMinDps(): number | null {
    const rate = this.selectedRoomFireRate;
    const minDmg = parseFloat(this.selectedRoomDesign?.minDamage ?? '0');
    if (!rate || rate <= 0 || !minDmg) return null;
    return minDmg / rate;
  }

  get selectedRoomMaxDps(): number | null {
    const rate = this.selectedRoomFireRate;
    const maxDmg = parseFloat(this.selectedRoomDesign?.maxDamage ?? '0');
    if (!rate || rate <= 0 || !maxDmg) return null;
    return maxDmg / rate;
  }
}
